import { defineStore } from 'pinia'
import { Assert, lazyRef } from '@previo/core'
import { usePlatform } from '@previo/design-system'
import { Context, DeviceType, UserId, useFeatureFlags } from '@previo/feature-flags'
import { initUserRealtime } from '@previo/realtime'
import { StoreKey } from '~/store/StoreKey'
import { useUserAPI } from '~/store/user/http/UserAPI'
import { usePropertyStore } from '~/store/property/property'
import { UserTypeId } from '~/codebooks/user/UserTypeId'
import { User } from '~/store/user/model/User'
import { syncLocaleWithUserLanguage } from '~/composables/translate'

export const useUserStore = defineStore(StoreKey.USER, () => {
  const loadUser = async () => {
    const user = await useUserAPI().load(usePropertyStore().isLoaded)
    const userModel = User.fromResponseData(user)

    Assert.notNull(userModel.id)
    Assert.notNull(userModel.languageId)

    syncLocaleWithUserLanguage(userModel.languageId)

    useFeatureFlags().appendContext(
      new Context([new DeviceType(usePlatform().getDeviceType()), new UserId(userModel.id)]),
    )

    initUserRealtime(userModel.realtime)

    return userModel
  }

  const user = lazyRef(loadUser, {} as User)

  function isRoot(): boolean {
    if (!user.isLoaded) {
      throw new Error('User store is not loaded')
    }

    return user.value.userTypeId === UserTypeId.ROOT
  }

  return {
    load: user.load,
    user,
    loaded: user.isLoaded,
    isRoot,
  }
})
